import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TSwissPostOrdersRequest, TSwissPostOrderRequest } from 'redux/reducers/shipments/types';
import {
	TEuDdpStore,
	TEuDdpOrderStatusesResponsePayload,
	TEuDdpOrderIdResponsePayload,
	TEuDdpOrderResponsePayload,
	TEuDdpRequestShipmentRequest,
	TEuDdpRequestShipmentResponsePayload,
	TEuDdpConfirmShipmentRequest,
	TEuDdpUpdateWeightRequest,
	TEuDdpUpdateDescriptionRequest,
	TEuDdpUpdateHs6CodeRequest,
	TEuDdpUpdateOrderFieldValueRequest,
	TEuDdpDownloadProformaInvoiceRequest,
	TEuDdpUploadProformaInvoiceRequest,
	TEuDdpGetSignedProformaInvoiceRequest,
	TEuDdpDownloadLabelRequest,
	TEuDdpDeliveryOperatorsResponsePayload,
	TEuDdpS10Request,
	TEuDdpS10ResponsePayload,
} from './types';

// ==========================================:
export const initialState: TEuDdpStore = {
	euDdpOrders: null,
	euDdpOrdersLoading: false,
	euDdpOrder: null,
	euDdpOrderError: null,
	euDdpOrderLoading: false,
	euDdpOrderStatuses: null,
	euDdpOrderStatusesLoading: false,
	euDdpRequestShipmentLoading: false,
	euDdpRequestShipmentError: null,
	euDdpConfirmShipmentLoading: false,
	euDdpConfirmShipmentError: null,
	euDdpUpdateWeightLoading: false,
	euDdpUpdateWeightError: null,
	euDdpUpdateDescriptionLoading: false,
	euDdpUpdateHs6CodeLoading: false,
	euDdpUpdateOrderFieldValueLoading: false,
	euDdpProformaInvoiceLoading: false,
	euDdpProformaInvoiceUploadLoading: false,
	euDdpSignedProformaInvoiceLoading: false,
	euDdpLabelLoading: false,
	euDdpRequestShipmentData: null,
	euDdpDeliveryOperators: null,
	euDdpDeliveryOperatorsLoading: false,
	euDdpS10Data: null,
	euDdpS10Loading: false,
	euDdpS10Error: null,
};

// ==========================================:
const euDdp = createSlice({
	name: '@@euDdp',
	initialState,
	reducers: {
		getEuDdpOrdersRequest: (state: TEuDdpStore, action: PayloadAction<TSwissPostOrdersRequest>) => {
			const euDdpState = state;
			euDdpState.euDdpOrdersLoading = true;
		},
		getEuDdpOrdersSuccess: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpOrderResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpOrders = action.payload;
			euDdpState.euDdpOrdersLoading = false;
		},
		getEuDdpOrderRequest: (state: TEuDdpStore, action: PayloadAction<TSwissPostOrderRequest>) => {
			const euDdpState = state;
			euDdpState.euDdpOrderLoading = true;
			euDdpState.euDdpOrderError = null;
		},
		getEuDdpOrderRequestWithoutReload: (
			state: TEuDdpStore,
			action: PayloadAction<TSwissPostOrderRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpOrderLoading = false;
			euDdpState.euDdpOrderError = null;
		},
		getEuDdpOrderSuccess: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpOrderIdResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpOrder = action.payload;
			euDdpState.euDdpOrderLoading = false;
			euDdpState.euDdpOrderError = null;
		},
		setEuDdpOrderError: (state: TEuDdpStore, action: PayloadAction<string | null>) => {
			const euDdpState = state;
			euDdpState.euDdpOrderError = action.payload;
			euDdpState.euDdpOrderLoading = false;
		},
		getEuDdpOrderStatusesRequest: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpOrderStatusesLoading = true;
		},
		getEuDdpOrderStatusesSuccess: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpOrderStatusesResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpOrderStatuses = action.payload;
			euDdpState.euDdpOrderStatusesLoading = false;
		},
		requestEuDdpShipmentRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpRequestShipmentRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpRequestShipmentLoading = true;
			euDdpState.euDdpRequestShipmentError = null;
			euDdpState.euDdpRequestShipmentData = null;
		},
		requestEuDdpShipmentSuccess: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpRequestShipmentResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpRequestShipmentLoading = false;
			euDdpState.euDdpRequestShipmentError = null;
			euDdpState.euDdpRequestShipmentData = action.payload;
		},
		setEuDdpRequestShipmentError: (state: TEuDdpStore, action: PayloadAction<string | null>) => {
			const euDdpState = state;
			euDdpState.euDdpRequestShipmentLoading = false;
			euDdpState.euDdpRequestShipmentError = action.payload;
		},
		confirmEuDdpShipmentRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpConfirmShipmentRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpConfirmShipmentLoading = true;
			euDdpState.euDdpConfirmShipmentError = null;
		},
		confirmEuDdpShipmentSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpConfirmShipmentLoading = false;
			euDdpState.euDdpConfirmShipmentError = null;
			euDdpState.euDdpRequestShipmentData = null;
		},
		setEuDdpConfirmShipmentError: (state: TEuDdpStore, action: PayloadAction<string | null>) => {
			const euDdpState = state;
			euDdpState.euDdpConfirmShipmentLoading = false;
			euDdpState.euDdpConfirmShipmentError = action.payload;
		},
		clearEuDdpRequestShipmentData: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpRequestShipmentData = null;
		},
		updateEuDdpWeightRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpUpdateWeightRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateWeightLoading = true;
			euDdpState.euDdpUpdateWeightError = null;
		},
		updateEuDdpWeightSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateWeightLoading = false;
		},
		updateEuDdpWeightFailure: (state: TEuDdpStore, action: PayloadAction<string>) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateWeightLoading = false;
			euDdpState.euDdpUpdateWeightError = { productId: action.payload, ts: Date.now() };
		},
		updateEuDdpDescriptionRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpUpdateDescriptionRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateDescriptionLoading = true;
		},
		updateEuDdpDescriptionSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateDescriptionLoading = false;
		},
		updateEuDdpHs6CodeRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpUpdateHs6CodeRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateHs6CodeLoading = true;
		},
		updateEuDdpHs6CodeSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateHs6CodeLoading = false;
		},
		updateEuDdpOrderFieldValueRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpUpdateOrderFieldValueRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateOrderFieldValueLoading = true;
		},
		updateEuDdpOrderFieldValueSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpUpdateOrderFieldValueLoading = false;
		},
		downloadEuDdpProformaInvoiceRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpDownloadProformaInvoiceRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpProformaInvoiceLoading = true;
		},
		downloadEuDdpProformaInvoiceSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpProformaInvoiceLoading = false;
		},
		uploadEuDdpProformaInvoiceRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpUploadProformaInvoiceRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpProformaInvoiceUploadLoading = true;
		},
		uploadEuDdpProformaInvoiceSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpProformaInvoiceUploadLoading = false;
		},
		getEuDdpSignedProformaInvoiceRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpGetSignedProformaInvoiceRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpSignedProformaInvoiceLoading = true;
		},
		getEuDdpSignedProformaInvoiceSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpSignedProformaInvoiceLoading = false;
		},
		downloadEuDdpLabelRequest: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpDownloadLabelRequest>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpLabelLoading = true;
		},
		downloadEuDdpLabelSuccess: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpLabelLoading = false;
		},
		getEuDdpDeliveryOperatorsRequest: (state: TEuDdpStore) => {
			const euDdpState = state;
			euDdpState.euDdpDeliveryOperatorsLoading = true;
		},
		getEuDdpDeliveryOperatorsSuccess: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpDeliveryOperatorsResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpDeliveryOperators = action.payload;
			euDdpState.euDdpDeliveryOperatorsLoading = false;
		},
		requestEuDdpS10Request: (state: TEuDdpStore, action: PayloadAction<TEuDdpS10Request>) => {
			const euDdpState = state;
			euDdpState.euDdpS10Loading = true;
			euDdpState.euDdpS10Error = null;
			euDdpState.euDdpS10Data = null;
		},
		requestEuDdpS10Success: (
			state: TEuDdpStore,
			action: PayloadAction<TEuDdpS10ResponsePayload>,
		) => {
			const euDdpState = state;
			euDdpState.euDdpS10Loading = false;
			euDdpState.euDdpS10Error = null;
			euDdpState.euDdpS10Data = action.payload;
		},
		setEuDdpS10Error: (state: TEuDdpStore, action: PayloadAction<string | null>) => {
			const euDdpState = state;
			euDdpState.euDdpS10Loading = false;
			euDdpState.euDdpS10Error = action.payload;
		},
		euDdpInitState: () => initialState,
	},
});

export default euDdp.reducer;
export const {
	getEuDdpOrdersRequest,
	getEuDdpOrdersSuccess,
	getEuDdpOrderRequest,
	getEuDdpOrderRequestWithoutReload,
	getEuDdpOrderSuccess,
	setEuDdpOrderError,
	getEuDdpOrderStatusesRequest,
	getEuDdpOrderStatusesSuccess,
	requestEuDdpShipmentRequest,
	requestEuDdpShipmentSuccess,
	setEuDdpRequestShipmentError,
	confirmEuDdpShipmentRequest,
	confirmEuDdpShipmentSuccess,
	setEuDdpConfirmShipmentError,
	clearEuDdpRequestShipmentData,
	updateEuDdpWeightRequest,
	updateEuDdpWeightSuccess,
	updateEuDdpWeightFailure,
	updateEuDdpDescriptionRequest,
	updateEuDdpDescriptionSuccess,
	updateEuDdpHs6CodeRequest,
	updateEuDdpHs6CodeSuccess,
	updateEuDdpOrderFieldValueRequest,
	updateEuDdpOrderFieldValueSuccess,
	downloadEuDdpProformaInvoiceRequest,
	downloadEuDdpProformaInvoiceSuccess,
	uploadEuDdpProformaInvoiceRequest,
	uploadEuDdpProformaInvoiceSuccess,
	getEuDdpSignedProformaInvoiceRequest,
	getEuDdpSignedProformaInvoiceSuccess,
	downloadEuDdpLabelRequest,
	downloadEuDdpLabelSuccess,
	getEuDdpDeliveryOperatorsRequest,
	getEuDdpDeliveryOperatorsSuccess,
	requestEuDdpS10Request,
	requestEuDdpS10Success,
	setEuDdpS10Error,
	euDdpInitState,
} = euDdp.actions;
